import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AuthenticityBand,
  CandidacyState,
  FunnelStage,
  FunnelSummary,
  JobPosting,
  OrgRole,
  PipelineStatus,
  Requisition,
} from "@talenthop/shared";
import {
  INTERVIEW_LANGUAGES,
  PIPELINE_STATUSES,
  PIPELINE_STATUS_LABEL,
  competencyById,
  orgRoleAtLeast,
  weightPercents,
  type InterviewLanguageCode,
} from "@talenthop/shared";
import { api, ApiError } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";
import { Arrow, Alert, Info, Users, Plus, RefreshCw, Copy, Trash, Edit, UploadCloud, Mail } from "../../components/icons.js";
import {
  ArrowRight,
  Briefcase,
  CalendarDays,
  Check,
  ChevronDown,
  Globe,
  IndianRupee,
  Lightbulb,
  ListChecks,
  Lock,
  MessagesSquare,
  Mic,
  MoreHorizontal,
  Pencil,
  Sliders,
  FileSpreadsheet,
  UserRound,
  X,
} from "lucide-react";
import { StageTracker, statePill, pipelineStatusPillClass, PROMPT_EDITOR_ROLES, REGION_LABEL } from "./pipeline.js";
import { parseCandidateLines } from "./candidateLines.js";
import { engagementLabel, engagementOf, engagementPillClass } from "./engagement.js";
import { ConfirmChargeModal } from "./ConfirmChargeModal.js";
import { serverMessage } from "./planUsage.js";
import { RequirementQuestionsModal } from "./RequirementQuestionsModal.js";
import { useEntitlements } from "./useEntitlements.js";
import { ImportsPanel } from "./ImportsPanel.js";
import type { Scan } from "./ResumeScoreList.js";
import { competencyLabel, useVerticals } from "./useVerticals.js";
import { AssessmentBars, AssessmentEditor, useAssessment } from "./AssessmentEditor.js";
import { CandidacyQuestionsPanel } from "./CandidacyQuestionsPanel.js";
import { languageLabel, normaliseLanguages } from "./interviewLanguages.js";
import { useTaxonomy } from "./useTaxonomy.js";
import { displayTitle } from "./displayTitle.js";
import { ImportCandidatesModal } from "./ImportCandidatesModal.js";
import { PublicShareButton } from "./PublicShareButton.js";
import { STATE_RANK, FUNNEL_STAGES, type FunnelStageKey } from "./funnelStages.js";
import { formatExperience, getRequisitionBudget, getRequisitionExperience, getSourcingType, hasConfiguredBudget } from "./requisitionFormat.js";
import { extractExperience } from "./promptAnalyzer.js";
import { suggestVerticalKey } from "./verticalSuggest.js";
import { Dropdown, type DropdownOption } from "../../components/Dropdown.js";
import {
  BudgetFields,
  CheckedSkillChips,
  COUNTRY_CURRENCY,
  ExperienceInput,
  LocationFields,
  budgetFromConfig,
  budgetProblem,
  budgetToConfig,
  joinLocation,
  splitLocation,
  useSkillChecks,
  workModeLabel,
  type Budget,
} from "./specFields.js";
import { BasicHtmlEditor } from "../../components/BasicHtmlEditor.js";
import { MarkdownView } from "../../components/MarkdownView.js";
import { MarkdownEditor } from "../../components/MarkdownEditor.js";
import { CompetencyPicker } from "../../components/CompetencyPicker.js";
import { SearchableSelect, DEPARTMENT_OPTIONS, INDUSTRY_OPTIONS } from "../../components/SearchableSelect.js";

/** "Other criteria" (#238): extra requirements saved with the requirement — visa sponsorship, a
 *  notice period when it matters, anything else. Stored as `config.otherCriteria`. */
interface OtherCriterion {
  label: string;
  value: string;
}
const CRITERION_PRESETS = ["Visa sponsorship", "Notice period", "Other"];

/** The four requirement-level statuses the page offers (#238). Every backend state still exists
 *  and is still set by the engine; the dropdown just groups them. */
type ReqStatus = "active" | "draft" | "on_hold" | "closed";
const REQ_STATUS_OPTIONS: DropdownOption<ReqStatus>[] = [
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
  { value: "on_hold", label: "On hold" },
  { value: "closed", label: "Closed" },
];
const reqStatusOf = (state: string): ReqStatus =>
  state === "draft" ? "draft" : state === "on_hold" ? "on_hold" : state === "closed" || state === "finalized" ? "closed" : "active";

interface FunnelCandidate {
  candidacyId: string;
  name: string | null;
  email?: string | null;
  state: CandidacyState;
  /** Recruiter-editable organizational tag (migration 0077) — separate from `state`; see
   *  PIPELINE_STATUSES' doc comment (packages/shared/src/types.ts). null = not tagged yet. */
  pipelineStatus?: PipelineStatus | null;
  resumeScore: number | null;
  /** When the candidacy was created — i.e. when the invite went out (QA #54). */
  createdAt?: string | null;
  invitedAt?: string | null;
  score?: number | null;
  band?: AuthenticityBand | null;
  /** Set ONLY when this candidate is assessed under a vertical the role has since moved off
   *  (migration 0079). Null on every candidate sharing the role's current vertical, so a list
   *  badges the exceptions rather than repeating the header on every row. */
  verticalKey?: string | null;
  verticalVersion?: number | null;
}

interface EditingTarget {
  type: "scan" | "candidate";
  id: string;
  name: string;
  email: string;
  filename?: string;
  resumeFile?: {
    filename: string;
    mime: string;
    contentBase64: string;
  };
  reScore?: boolean;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const COMMON_SKILL_CATALOG = [
  "React", "Next.js", "Vue.js", "Angular", "TypeScript", "JavaScript", "Node.js", "Express",
  "Python", "Django", "FastAPI", "Flask", "Java", "Spring Boot", "Go", "Golang", "Rust",
  "C++", "C#", ".NET", "PHP", "Laravel", "Ruby on Rails", "SQL", "PostgreSQL", "MySQL",
  "MongoDB", "Redis", "Elasticsearch", "Kafka", "RabbitMQ", "GraphQL", "REST APIs", "gRPC",
  "Docker", "Kubernetes", "AWS", "Azure", "GCP", "CI/CD", "Terraform", "Microservices",
  "System Design", "Distributed Systems", "HTML5", "CSS3", "Tailwind CSS", "Redux",
  "Jest", "Cypress", "Playwright", "Git", "Machine Learning", "PyTorch", "TensorFlow",
  "LLMs", "Generative AI", "NLP", "Data Engineering", "Snowflake", "dbt", "Spark",
  "Linux", "Security", "Agile", "Scrum", "Product Management", "Figma", "UI/UX"
];

function extractSuggestedSkills(title: string, summary: string, existingSkills: string[]): string[] {
  const combined = `${title || ""} ${summary || ""}`.toLowerCase();
  const existingLower = new Set(existingSkills.map((s) => s.toLowerCase()));
  const matches: string[] = [];

  for (const skill of COMMON_SKILL_CATALOG) {
    if (existingLower.has(skill.toLowerCase())) continue;
    const escaped = skill.toLowerCase().replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
    const regex = new RegExp(`(^|[^a-z0-9#+.-])${escaped}([^a-z0-9#+.-]|$)`, "i");
    if (regex.test(combined)) {
      matches.push(skill);
    }
  }
  return matches;
}

interface FunnelResponse {
  // `verticalName` rides along with the funnel so the header doesn't have to wait on /verticals to
  // name the vocabulary. Optional so an older payload still renders (falls back to the key).
  // `verticalEditionResolved` says whether `verticalVersion` resolves to a stored edition
  // (vertical_editions, migration 0042) — the header prints the number only when it does.
  // Taxonomy fields (0045) are optional AND nullable: a requisition created before the industry/
  // role axis existed renders no taxonomy pills at all — never a fabricated placeholder.
  requisition: Requisition & {
    verticalName?: string;
    verticalEditionResolved?: boolean;
    industryKey?: string | null;
    roleFamilyKey?: string | null;
    specialisationKeys?: string[];
    jobDescription?: string | null;
    /** Who raised it — decides whether Delete is offered here (defect 44). The API re-checks. */
    createdBy?: string | null;
    /** #238: the creator's name for "Created by" in the header. */
    createdByName?: string | null;
    /** 0113: set when an admin switched this job from We source to I'll source. */
    sourcingConvertedAt?: string | null;
    /** Owner-controlled per-org flags (migration 0075) — gates the custom judge-prompt editor
     *  below. Optional: absent (or a mid-deploy API instance that predates this field) degrades
     *  to every flag off, same defaults the migration ships. */
    flags?: { rescoreEnabled: boolean; customPromptEnabled: boolean; copyQuestionsEnabled: boolean };
    /** 0083 email intake: this job's own receiving address, and the code behind it. Both null
     *  on a requisition created before the connector framework existed. */
    jobCode?: string | null;
    jobEmail?: string | null;
  };
  stages: FunnelStage[];
  candidates: FunnelCandidate[];
  postings: JobPosting[];
  tranche: FunnelSummary;
  deliveriesCount: number;
  // Wave 3 (vendor flow) — optional; surfaced by the API for staff_aug requisitions.
  vendors?: { id: string; name: string }[]; // linked vendors the owner may assign
  fulfilledByOrgName?: string | null; // vendor fulfilling this req (owner view)
  clientOrgName?: string | null; // end_customer that owns this req (vendor view)
}
interface AddedCandidate {
  candidacyId: string;
  inviteToken: string;
  inviteUrl: string;
  name?: string;
  email?: string | null;
  emailed?: boolean;
}
interface AddCandidatesResponse {
  added: AddedCandidate[];
}


/** Interview finished (or past it) — an absent score here means "being computed", not "not yet". */
const INTERVIEW_DONE_STATES = new Set(["submitted", "scored", "advanced", "rejected", "completed"]);

const BAND_PILL: Record<AuthenticityBand, { cls: string; label: string }> = {
  verified: { cls: "pill v", label: "Verified" },
  needs_review: { cls: "pill r", label: "Needs review" },
  failed: { cls: "pill f", label: "Failed" },
};

/** Local confirm modal — mirrors the Review.tsx pattern (no shared modal class exists yet). */
function ConfirmModal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <div
      className="t1-confirm-scrim"
      onClick={onClose}
    >
      <div
        className="card t1-confirm-card"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-h">
          <h3>{title}</h3>
          <button type="button" className="t1-icon-btn" onClick={onClose} aria-label="Close" data-tip="Close" data-tip-pos="left">
            <X aria-hidden="true" />
          </button>
        </div>
        <div className="card-b">{children}</div>
      </div>
    </div>
  );
}

interface JudgePromptResponse {
  requisitionId: string;
  lockedSuffix: string;
  defaultBody: string;
  customBody: string | null;
  /** 0100: written for the pre-0099 scoring contract — stored but NOT applied until saved again. */
  customBodyLegacy?: boolean;
  updatedAt: string | null;
}

/**
 * Org-facing per-requisition judge-prompt editor (GET/PUT /requisitions/{id}/judge-prompt) — only
 * mounted when the owner has turned `customPromptEnabled` on for this org (the caller checks the
 * funnel payload's flag before rendering this) AND the signed-in member is `hiring_manager` or
 * `super_admin` (checked here too, so a recruiter/interviewer never sees a form that would just
 * 403 — the API re-checks regardless, this is UX only). Deliberately separate from the
 * platform-wide /admin/prompts editor: THIS requisition's override wins outright over the
 * org/vertical/platform/default chain (apps/worker/src/modelPrompts.ts) for every candidate on
 * it; the other four prompt keys stay owner-only.
 */
function JudgePromptCard({ requisitionId }: { requisitionId: string }): ReactNode {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const query = useQuery({
    queryKey: ["judge-prompt", requisitionId],
    queryFn: () => api.get<JudgePromptResponse>(`/requisitions/${requisitionId}/judge-prompt`),
  });

  const mutation = useMutation({
    mutationFn: (promptText: string | null) =>
      api.put(`/requisitions/${requisitionId}/judge-prompt`, { promptText }),
    onSuccess: () => {
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1600);
      void queryClient.invalidateQueries({ queryKey: ["judge-prompt", requisitionId] });
    },
  });

  // Client-side gate is UX only — hides the form from a role that would just get a 403. The API
  // (requisitionPrompt.ts) enforces the real bar.
  if (!me?.role || !PROMPT_EDITOR_ROLES.has(me.role)) return null;

  const data = query.data;
  const text = draft ?? data?.customBody ?? "";

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-h">
        <h3>Custom judge prompt</h3>
        <span className="tag">interview scoring — overrides the platform default for THIS requisition only</span>
      </div>
      <div className="card-b" style={{ display: "grid", gap: 10 }}>
        {query.isLoading && (
          <p className="mut sm" style={{ margin: 0 }}>
            Loading…
          </p>
        )}
        {query.isError && (
          <p className="mut sm" style={{ margin: 0 }}>
            {query.error instanceof ApiError ? query.error.message : "Couldn't load the judge prompt."}
          </p>
        )}
        {data && (
          <>
            <p className="mut sm" style={{ margin: 0 }}>
              {data.customBody
                ? `Custom prompt in place${data.updatedAt ? ` · last edited ${new Date(data.updatedAt).toLocaleString()}` : ""}.`
                : "No custom prompt yet — every candidate here is judged under the platform default below."}
            </p>
            {data.customBodyLegacy && (
              <div className="banner warn" style={{ margin: 0 }}>
                <span>
                  <b>This custom prompt is inactive.</b> It was written for the old per-question scoring and would
                  contradict the evidence-based evaluation, so the platform default runs instead. Review it and save
                  it again to use it.
                </span>
              </div>
            )}
            <textarea
              className="rq-in"
              style={{ minHeight: 160, fontFamily: "var(--mono)", fontSize: 12.5 }}
              placeholder="Leave blank to keep using the platform default"
              value={text}
              onChange={(e) => setDraft(e.target.value)}
            />
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn sm"
                disabled={mutation.isPending || text.trim().length < 20}
                onClick={() => mutation.mutate(text.trim())}
              >
                {mutation.isPending ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                className="btn sm ghost"
                disabled={mutation.isPending}
                onClick={() => setDraft(data.defaultBody)}
              >
                Copy in the platform default
              </button>
              {data.customBody && (
                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={mutation.isPending}
                  onClick={() => {
                    if (
                      window.confirm(
                        "Clear this requisition's custom prompt and go back to the platform default?",
                      )
                    ) {
                      setDraft(null);
                      mutation.mutate(null);
                    }
                  }}
                >
                  Reset to platform default
                </button>
              )}
              {saved && <span className="mut sm">Saved.</span>}
              {mutation.isError && (
                <span style={{ color: "var(--crit)" }}>
                  {mutation.error instanceof ApiError ? mutation.error.message : "Couldn't save."}
                </span>
              )}
            </div>
            <details>
              <summary className="mut sm" style={{ cursor: "pointer" }}>
                Platform rules appended after this (read-only, always on)
              </summary>
              <pre
                style={{
                  fontFamily: "var(--mono)",
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                  color: "var(--ink-mute)",
                  marginTop: 8,
                }}
              >
                {data.lockedSuffix}
              </pre>
            </details>
          </>
        )}
      </div>
    </div>
  );
}

/** Users · live funnel for one requisition (GET /requisitions/{id}/funnel): stage tracker,
 *  cumulative funnel counts, tranche/self-heal banners, postings, ranked candidates → review. */
export function RequisitionDetail(): ReactNode {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { me } = useAuth();

  const [pasteText, setPasteText] = useState("");
  const [jobEmailCopied, setJobEmailCopied] = useState(false);
  const [jobEmailFailed, setJobEmailFailed] = useState(false);
  const [addCandidatesModalOpen, setAddCandidatesModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [added, setAdded] = useState<AddedCandidate[]>([]);
  const [closeOpen, setCloseOpen] = useState(false);
  const [weightsOpen, setWeightsOpen] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const [vendorChoice, setVendorChoice] = useState("");
  const [chargeOpen, setChargeOpen] = useState(false);
  const [questionsFor, setQuestionsFor] = useState<{ id: string; name: string | null } | null>(null);
  /**
   * The REQUISITION-level shared question plan. QA (4 Aug): "no option to view the questions or
   * modify them after created" — correct, and it was a dead end. QuestionPlanEditor existed but
   * was mounted only inside the New Request wizard, so once the request was created the shared
   * plan (the one every candidate without a tailored plan is asked) had no route at all.
   */
  const [planOpen, setPlanOpen] = useState(false);
  // Deep-links from the requisitions list's row menu (QA #86): ?questions=1 / ?close=1 open the
  // matching modal on arrival, then drop out of the URL so refresh doesn't re-open them.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("questions") === "1") setPlanOpen(true);
    if (searchParams.get("close") === "1") setCloseOpen(true);
    if (searchParams.get("questions") || searchParams.get("close")) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [repointTo, setRepointTo] = useState("");
  const [repointOpen, setRepointOpen] = useState(false);
  const [repointMsg, setRepointMsg] = useState<{ tone: "warn" | "crit" | "info"; text: string } | null>(null);
  const [editingTarget, setEditingTarget] = useState<EditingTarget | null>(null);

  const updateCandidateDetails = useMutation({
    mutationFn: async (target: EditingTarget) => {
      if (target.type === "scan") {
        return api.patch(`/requisitions/${id}/resumes/${target.id}`, {
          candidateName: target.name,
          candidateEmail: target.email,
          resumeFile: target.resumeFile,
          reScore: target.reScore !== false,
        });
      } else {
        return api.patch(`/requisitions/${id}/candidates/${target.id}`, {
          candidateName: target.name,
          candidateEmail: target.email,
        });
      }
    },
    onSuccess: () => {
      setEditingTarget(null);
      void queryClient.invalidateQueries({ queryKey: ["funnel", id] });
      void queryClient.invalidateQueries({ queryKey: ["reqScans", id] });
    },
  });

  // Edit the title/JD (PATCH /requisitions/{id}) — the only requisition fields with no scoring or
  // question-plan stake, so they're editable at any state, unlike vertical/competencies below.
  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editJd, setEditJd] = useState("");
  const [editMsg, setEditMsg] = useState<{ tone: "warn" | "crit" | "info"; text: string } | null>(null);

  const [isEditingSpec, setIsEditingSpec] = useState(false);
  const [newSkillInput, setNewSkillInput] = useState("");
  const [specDraft, setSpecDraft] = useState({
    title: "",
    department: "",
    industry: "",
    experience: "",
    location: "",
    place: { city: "", country: "" },
    mode: "",
    summary: "",
    requiredSkills: [] as string[],
    interviewLanguages: ["en"] as string[],
    competencies: [] as string[],
    otherCriteria: [] as OtherCriterion[],
  });
  const [budgetDraft, setBudgetDraft] = useState<Budget>({ currency: "INR", min: "", max: "", period: "year" });
  const skillChecks = useSkillChecks();

  // 0083: is this org actually collecting applications by email? The address is only shown when
  // it is — an address nothing is collecting would swallow real applications silently. A failed
  // or forbidden read degrades to "not live", which hides the card rather than over-promising.
  const connectorsQuery = useQuery({
    queryKey: ["connectors", me?.orgId ?? ""],
    queryFn: () =>
      api.get<{ connectors: { typeKey: string; status: string; enabled: boolean }[] }>(
        `/orgs/${me?.orgId}/connectors`,
      ),
    enabled: Boolean(me?.orgId),
    staleTime: 60_000,
    retry: false,
  });
  const emailIntakeLive = (connectorsQuery.data?.connectors ?? []).some(
    (c) => c.typeKey === "email_receive" && c.enabled && c.status === "active",
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ["funnel", id],
    queryFn: () => api.get<FunnelResponse>(`/requisitions/${id}/funnel`),
    enabled: id.length > 0,
    refetchInterval: 15_000, // the engine moves candidacies on its own — keep the funnel alive
  });

  const req = data?.requisition;

  const suggestedSkills = useMemo(() => {
    return extractSuggestedSkills(specDraft.title, specDraft.summary, specDraft.requiredSkills);
  }, [specDraft.title, specDraft.summary, specDraft.requiredSkills]);

  useEffect(() => {
    if (req) {
      const cfg = (req.config || {}) as Record<string, any>;
      // Experience as people write it ("5+ years"), from what was typed — never an "L2" code.
      const exp = formatExperience(getRequisitionExperience({ title: req.title, config: cfg }));
      const skills = cfg.requiredSkills || cfg.skills || [];
      const langs = cfg.interviewLanguages || ["en"];
      const loc = cfg.location || (req.region ? (REGION_LABEL[req.region] ?? req.region) : "India");
      // New requirements save `workplaceMode`; this page used to save `mode` — read both.
      const mode = cfg.workplaceMode || cfg.mode || "Hybrid";
      const dep = cfg.department || "Engineering";
      const ind = cfg.industry || req.industryKey || "Technology / Software";
      const sum = req.jobDescription || cfg.summary || "";
      const fallbackCountry = req.region === "in-central" || cfg.salaryCurrency === "INR" ? "India" : "";
      const split = splitLocation(loc, fallbackCountry);
      const place = {
        city: typeof cfg.city === "string" ? cfg.city : split.city,
        country: typeof cfg.country === "string" ? cfg.country : split.country,
      };
      const other: OtherCriterion[] = Array.isArray(cfg.otherCriteria)
        ? (cfg.otherCriteria as unknown[]).flatMap((c) => {
            const o = c as Partial<OtherCriterion> | null;
            return o && typeof o.label === "string" && typeof o.value === "string" ? [{ label: o.label, value: o.value }] : [];
          })
        : [];

      setSpecDraft({
        title: req.title,
        department: dep,
        industry: ind,
        experience: exp,
        location: loc,
        place,
        mode: mode,
        summary: sum,
        requiredSkills: Array.isArray(skills) ? skills : [],
        interviewLanguages: Array.isArray(langs) ? langs : ["en"],
        competencies: Array.isArray(cfg.competencies) ? cfg.competencies : [],
        otherCriteria: other,
      });
      setBudgetDraft(budgetFromConfig(cfg, COUNTRY_CURRENCY[place.country] ?? "INR"));
      skillChecks.reset();
      setEditTitle(req.title);
      setEditJd(req.jobDescription ?? "");
    }
  }, [req]);

  /**
   * DEFECT 45 — the resumes a recruiter uploaded used to VANISH on reload.
   *
   * They were scored, stored in resume_scans and billed, but this page only ever rendered
   * candidacies — and a candidacy is created at the SELECT step, not at upload. So a recruiter who
   * uploaded ten resumes, navigated away and came back was told "No candidates yet", with no route
   * back to work they had already paid for. The endpoint existed the whole time; nothing called it.
   */
  const scansQuery = useQuery({
    queryKey: ["reqScans", id],
    queryFn: () => api.get<{ scans: Scan[] }>(`/requisitions/${id}/resumes`),
    enabled: id.length > 0,
  });
  const scans = scansQuery.data?.scans ?? [];
  const unselectedScans = scans.filter((s) => !s.candidacyId);

  // QA #58 residual: screened-but-uninvited resumes were visible here but could only be invited
  // from the wizard, which never reloads them. This is the missing "pick who to interview" —
  // same endpoint the wizard uses, same charge-confirm ritual as invite-more below.
  const [pickedScanIds, setPickedScanIds] = useState<Set<string>>(new Set());
  const [scanChargeOpen, setScanChargeOpen] = useState(false);
  const togglePickedScan = (scanId: string): void =>
    setPickedScanIds((prev) => {
      const next = new Set(prev);
      if (next.has(scanId)) next.delete(scanId);
      else next.add(scanId);
      return next;
    });
  const inviteScans = useMutation({
    mutationFn: () =>
      api.post<{ added: unknown[]; skipped: number }>(`/requisitions/${id}/resumes/select`, {
        scanIds: Array.from(pickedScanIds),
        competencies: data?.requisition?.config.competencies ?? [],
      }),
    onSuccess: () => {
      setPickedScanIds(new Set());
      setScanChargeOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["reqScans", id] });
      void queryClient.invalidateQueries({ queryKey: ["funnel", id] });
    },
  });

  const changeState = useMutation({
    mutationFn: (newState: string) => api.post(`/requisitions/${id}/state`, { state: newState }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["funnel", id] });
    },
  });

  // Live adaptive follow-up questions ("a moment ago you said...") — on by default, one switch
  // for the whole requisition. Only affects sessions that start after the change.
  const setAdaptiveQuestions = useMutation({
    mutationFn: (enabled: boolean) =>
      api.patch(`/requisitions/${id}/adaptive-questions`, { enabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["funnel", id] });
    },
  });

  // Resume-tailored questions — on by default. Off = every candidate gets the shared plan (and with
  // adaptive follow-ups also off, the exact same questions). Same 402 lock as adaptive on FREE orgs.
  const setResumeQuestions = useMutation({
    mutationFn: (enabled: boolean) =>
      api.patch(`/requisitions/${id}/resume-questions`, { enabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["funnel", id] });
    },
  });
  // FREE orgs (self-serve, never paid) have both switches OFF and locked; any paid org has both.
  const { entitlements } = useEntitlements();

  // The vertical this role was assessed under, resolved from the requisition's PINNED key — the
  // funnel names it for us, but only the vertical itself carries the competency LABELS.
  const { byKey: verticalByKey, verticals } = useVerticals();
  const vertical = verticalByKey(req?.verticalKey);

  // The requisition may have NO competencies configured yet (a gap the wizard/edit form used to
  // leave with no fix at all) — the verticals list loads separately from `req`, so once it's ready,
  // backfill a suggested selection (the vertical's own competencies) into the draft ONLY while it's
  // still empty, so this never overwrites a real saved selection or a hand-edit in progress.
  useEffect(() => {
    if (!req || specDraft.competencies.length > 0 || verticals.length === 0) return;
    if (vertical) {
      setSpecDraft((s) =>
        s.competencies.length > 0 ? s : { ...s, competencies: vertical.competencies.map((c) => c.competencyId).slice(0, 12) },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req, verticals, vertical]);

  // Industry + role family (0045) — display vocabulary only; resolves keys to names.
  const { industryName, familyName, specName } = useTaxonomy();

  /**
   * Re-point this role at a different vertical (PUT /requisitions/{id}/vertical).
   *
   * The escape hatch for #26: migration 0037 had to put every legacy `technical`/`skillset` scope
   * on `software_engineering`, so a Business Analyst raised under the old model came out assessed
   * on coding fundamentals. Nothing but a human reading the role can tell that from a real backend
   * requisition, which is why this is a deliberate action and not a re-mapping.
   *
   * The API owns the guard, and since migration 0079 it is a WARNING rather than a refusal. A
   * candidate keeps their own pinned vertical from the moment their questions are materialised, so
   * a re-point moves the role and everyone nobody has questioned yet, and leaves the rest exactly
   * where they were assessed. The API answers 409 `confirm_required` with the split spelled out —
   * who stays, how many of them are scored, and which question plans are discarded. This form
   * relays it verbatim: it is written for the person reading it.
   */
  const repoint = useMutation({
    mutationFn: (body: { verticalKey: string; confirm?: boolean }) =>
      api.put<{ requisition: unknown; discardedPlans: number; evaluationsOutdated: number }>(
        `/requisitions/${id}/vertical`,
        body,
      ),
    onSuccess: (res) => {
      setRepointOpen(false);
      setRepointMsg({
        tone: "info",
        text: [
          "Re-pointed.",
          res.discardedPlans > 0
            ? `${res.discardedPlans} question plan${res.discardedPlans === 1 ? " was" : "s were"} discarded and will regenerate under the new vertical.`
            : null,
          res.evaluationsOutdated > 0
            ? `${res.evaluationsOutdated} evaluated candidate${res.evaluationsOutdated === 1 ? " is" : "s are"} now on older criteria — their results are kept; re-evaluate them from Assessment criteria to compare on the new set. Nobody is re-interviewed.`
            : null,
        ]
          .filter(Boolean)
          .join(" "),
      });
      void queryClient.invalidateQueries({ queryKey: ["funnel", id] });
      void queryClient.invalidateQueries({ queryKey: ["assessment", id] });
      void queryClient.invalidateQueries({ queryKey: ["requisitions"] });
    },
    onError: (err: unknown) => {
      const body = (err as { body?: { error?: string; message?: string } }).body;
      // `confirm_required` is not a failure, it is the API telling the owner what will be lost.
      setRepointMsg({
        tone: body?.error === "confirm_required" ? "warn" : "crit",
        text: body?.message ?? "Couldn't change the vertical. Please retry in a moment.",
      });
    },
  });
  const needsConfirm =
    (repoint.error as { body?: { error?: string } } | null)?.body?.error === "confirm_required";

  const updateReq = useMutation({
    mutationFn: (body: { title?: string; jobDescription?: string; config?: Record<string, unknown> }) =>
      api.patch<{ requisition: Requisition }>(`/requisitions/${id}`, body),
    onSuccess: () => {
      setIsEditingSpec(false);
      setEditOpen(false);
      setEditMsg({ tone: "info", text: "Saved job specification successfully." });
      void queryClient.invalidateQueries({ queryKey: ["funnel", id] });
      void queryClient.invalidateQueries({ queryKey: ["requisitions"] });
    },
    onError: (err: unknown) => {
      const body = (err as { body?: { message?: string } })?.body;
      setEditMsg({ tone: "crit", text: body?.message ?? "Couldn't save. Please retry in a moment." });
    },
  });

  const specSkills = skillChecks.approved(specDraft.requiredSkills);
  /** Add a must-have skill and have the AI check it against this role (#238). */
  const addSpecSkill = (raw: string): void => {
    const skill = raw.trim().replace(/\s+/g, " ").slice(0, 80);
    if (!skill || specDraft.requiredSkills.some((k) => k.toLowerCase() === skill.toLowerCase())) return;
    setSpecDraft((s) => ({ ...s, requiredSkills: [...s.requiredSkills, skill] }));
    void skillChecks.check(skill, {
      title: specDraft.title,
      department: specDraft.department,
      industry: specDraft.industry,
      experience: specDraft.experience,
      location: joinLocation(specDraft.place.city, specDraft.place.country),
      workplaceMode: specDraft.mode,
      skills: specSkills,
      summary: specDraft.summary,
    });
  };
  const specBudgetProblem = budgetProblem(budgetDraft);
  const handleSaveSpec = () => {
    const location = joinLocation(specDraft.place.city, specDraft.place.country);
    // The assessment vertical is no longer picked by hand (#238): it follows Department +
    // Industry. It is re-pointed ONLY when one of those two actually changed here — a role is never
    // moved under its candidates just because the mapping rules improved.
    const cfg = (req?.config ?? {}) as Record<string, unknown>;
    const deptOrIndustryChanged =
      specDraft.department !== (cfg.department ?? "") || specDraft.industry !== (cfg.industry ?? req?.industryKey ?? "");
    const suggestedVertical =
      verticals.length > 0
        ? suggestVerticalKey(
            { department: specDraft.department, industry: specDraft.industry, title: specDraft.title },
            verticals.map((v) => v.key),
          )
        : null;
    const verticalChanging = Boolean(deptOrIndustryChanged && suggestedVertical && suggestedVertical !== req?.verticalKey);
    updateReq.mutate({
      title: specDraft.title.trim(),
      jobDescription: specDraft.summary,
      config: {
        department: specDraft.department,
        industry: specDraft.industry,
        experience: specDraft.experience,
        targetExperience: specDraft.experience,
        // Interview depth codes from the typed years — the old save wrote "5+ years" into `levels`.
        levels: extractExperience(specDraft.experience).levels,
        ...budgetToConfig(budgetDraft),
        location,
        country: specDraft.place.country || undefined,
        city: specDraft.place.city || undefined,
        workplaceMode: specDraft.mode,
        mode: specDraft.mode,
        summary: specDraft.summary,
        // An AI-rejected skill is never saved as a screening skill (#238).
        skills: specSkills,
        requiredSkills: specSkills,
        interviewLanguages: specDraft.interviewLanguages,
        otherCriteria: specDraft.otherCriteria
          .map((c) => ({ label: c.label.trim(), value: c.value.trim() }))
          .filter((c) => c.label && c.value),
        // Omit rather than send [] (or during a vertical change): the API's competencies field is
        // min(1) when present, and an old-vertical selection means nothing under a new one.
        ...(!verticalChanging && specDraft.competencies.length > 0
          ? { competencies: specDraft.competencies }
          : {}),
      },
    });
    if (verticalChanging && suggestedVertical) {
      // No `confirm`: when candidates are already assessed the API answers confirm_required with
      // exactly what would move, and the banner below lets the owner decide.
      repoint.mutate({ verticalKey: suggestedVertical });
    }
  };
  const stages = data?.stages ?? [];
  const candidates = data?.candidates ?? [];
  // Candidates the API says are pinned to a vertical this role has moved off (migration 0079).
  // They are the honest cost of a re-point: still on the role, no longer comparable to the rest.
  const assessedElsewhere = candidates.filter((c) => !!c.verticalKey);
  const [statusFilter, setStatusFilter] = useState<PipelineStatus | "all" | "untagged">("all");
  // Filtered by the recruiter's own pipeline-status tag BEFORE pagination, so "load more" pages
  // through the filtered set, not the whole funnel.
  const statusFiltered =
    statusFilter === "all"
      ? candidates
      : statusFilter === "untagged"
        ? candidates.filter((c) => !c.pipelineStatus)
        : candidates.filter((c) => c.pipelineStatus === statusFilter);
  /**
   * A sourcing run puts hundreds of candidates on this page and the table rendered
   * every one, so the request turned into a scroll with no way to reach the footer.
   * Show a page at a time; the ordering upstream already puts the interesting rows
   * first, so "load more" never hides a decision the recruiter needs.
   */
  const [candidatePage, setCandidatePage] = useState(1);
  const CANDIDATES_PER_PAGE = 25;
  const visibleCandidates = statusFiltered.slice(0, candidatePage * CANDIDATES_PER_PAGE);
  const moreCandidates = statusFiltered.length - visibleCandidates.length;
  const tranche = data?.tranche;
  const postings = data?.postings ?? [];

  const countAtLeast = (rank: number): number =>
    stages.reduce((a, st) => a + ((STATE_RANK[st.stage] ?? 0) >= rank ? st.count : 0), 0);

  // Post mode runs the full boards→screen→invite funnel. Uploaded/direct candidates enter at
  // `invited` (they never sourced or screened), so their funnel drops Applied-via-boards + Screened
  // and shows the show rate only where it's a real measured metric (post mode).
  // Each card opens the list behind its number, filtered to exactly the people it counts
  // (`?stage=`, funnelStages.ts): the Candidates page for the stages before the interview, the
  // Interviews page for Interviewed and Finalists.
  const card = (key: FunnelStageKey, note: string, gate?: boolean) => {
    const { rank, label } = FUNNEL_STAGES[key];
    const onInterviews = rank >= FUNNEL_STAGES.interviewed.rank;
    return {
      n: countAtLeast(rank),
      label,
      note,
      gate,
      href: `/app/${onInterviews ? "interviews" : "candidates"}?reqId=${id}&stage=${key}`,
      view: onInterviews ? "View interviews" : "View candidates",
    };
  };
  const funnelRow: { n: number; label: string; note: string; gate?: boolean; href: string; view: string }[] =
    req?.config.entryMode === "post"
      ? [
        card("applied", "via region boards"),
        card("screened", "rubric-scored"),
        card("invited", tranche ? `show rate ${Math.round(tranche.showRate * 100)}%` : ""),
        card("scheduled", "self-booked"),
        // "scored", not "verified & scored": identity verification is per-candidate and may be
        // simulated/off (pilot default), so a stage-wide "verified" label would overclaim.
        card("interviewed", "AI-scored"),
        card("finalists", "awaiting you", true),
      ]
      : [
        card("uploaded", "by you"),
        card("invited", "link sent by you"),
        card("scheduled", "self-booked"),
        card("interviewed", "verified & scored"),
        card("finalists", "awaiting you", true),
      ];

  const parsed = parseCandidateLines(pasteText);
  // Adding candidates mints verified interviews — a charged action, so it must be confirmed through
  // the same pay-before-value modal the New Request flow uses. PLANS (0094): each one uses an AI
  // interview from the organisation's allowance, confirmed in the usage modal before anything runs.
  const addCount = parsed.candidates.length;
  const inviteMore = useMutation({
    mutationFn: () =>
      api.post<AddCandidatesResponse>(`/requisitions/${id}/candidates`, {
        candidates: parsed.candidates,
      }),
    onSuccess: (res) => {
      setAdded(res.added);
      setPasteText("");
      setChargeOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["funnel", id] });
    },
  });

  const short = tranche ? Math.max(0, tranche.target - tranche.completed) : 0;

  const isPost = req?.config.entryMode === "post";
  // A request is done enough to close once the engine has produced its shortlist.
  const canClose = req && req.state !== "closed" && req.state !== "finalized";

  const closeReq = useMutation({
    mutationFn: () =>
      api.post<{ requisition: Requisition }>(`/requisitions/${id}/close`, {
        reason: closeReason.trim() || undefined,
      }),
    onSuccess: () => {
      setCloseOpen(false);
      setCloseReason("");
      void queryClient.invalidateQueries({ queryKey: ["funnel", id] });
      void queryClient.invalidateQueries({ queryKey: ["requisitions"] });
      void queryClient.invalidateQueries({ queryKey: ["orgStats"] });
    },
  });

  const holdReq = useMutation({
    mutationFn: () => api.post(`/requisitions/${id}/hold`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["funnel", id] });
      void queryClient.invalidateQueries({ queryKey: ["requisitions"] });
    },
  });

  const resumeReq = useMutation({
    mutationFn: () => api.post(`/requisitions/${id}/resume`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["funnel", id] });
      void queryClient.invalidateQueries({ queryKey: ["requisitions"] });
    },
  });

  const reopenReq = useMutation({
    mutationFn: () => api.post(`/requisitions/${id}/reopen`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["funnel", id] });
      void queryClient.invalidateQueries({ queryKey: ["requisitions"] });
    },
  });

  // ---- Wave 3: engagement + vendor fulfilment ----
  const myOrgId = me?.orgId ?? null;
  const engagement = req ? engagementOf(req) : "fte";
  const isStaffAug = engagement === "staff_aug";
  const isOwner = !!(req && myOrgId && req.orgId === myOrgId);
  const isFulfillingVendor = !!(
    req &&
    myOrgId &&
    req.fulfilledByOrgId === myOrgId &&
    req.orgId !== myOrgId
  );
  const linkedVendors = data?.vendors ?? [];
  const fulfilledByName =
    data?.fulfilledByOrgName ??
    linkedVendors.find((v) => v.id === req?.fulfilledByOrgId)?.name ??
    null;

  const assignVendor = useMutation({
    mutationFn: (vendorOrgId: string) =>
      api.post<{ requisition: Requisition }>(`/requisitions/${id}/assign-vendor`, { vendorOrgId }),
    onSuccess: () => {
      setVendorChoice("");
      void queryClient.invalidateQueries({ queryKey: ["funnel", id] });
      void queryClient.invalidateQueries({ queryKey: ["requisitions"] });
    },
  });

  /**
   * Persist an edit to the SHARED question plan. The wizard defers its PUT until "send"; there is
   * no send here, so each edit is written straight through. Debounced so typing in a prompt does
   * not fire a request per keystroke.
   */
  /**
   * DEFECT 46/47 — what the recruiter saves here is now what the candidate is ASKED.
   *
   * Saving marks the shared plan 'manual', which makes it authoritative: per-candidate resume
   * tailoring stands down for this request, and any machine-written plan already waiting on a
   * not-yet-started candidate is cleared so the save reaches them. `appliedTo` is how many that
   * was — the recruiter should see the save land on real people, not just on a row in a table.
   */
  const savePlan = useMutation({
    mutationFn: (questions: unknown[]) =>
      api.put<{ source?: "generated" | "manual"; appliedTo?: number }>(
        `/requisitions/${id}/questions`,
        { questions },
      ),
    onSuccess: (res) => {
      setPlanSaved(Date.now());
      setPlanApplied(res?.appliedTo ?? null);
      void queryClient.invalidateQueries({ queryKey: ["question-plan", id] });
    },
  });
  const [planApplied, setPlanApplied] = useState<number | null>(null);
  // Shares the editor's query key + fetcher, so this dedupes rather than adding a second request.
  const planMeta = useQuery({
    queryKey: ["question-plan", id],
    queryFn: () =>
      api.get<{ questions: unknown[]; source?: "generated" | "manual" }>(
        `/requisitions/${id}/questions`,
      ),
    enabled: planOpen && id.length > 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const planIsManual = planMeta.data?.source === "manual";
  const [planSaved, setPlanSaved] = useState<number | null>(null);
  const planTimer = useRef<number | null>(null);
  const saveSharedPlan = useCallback(
    (questions: unknown[]): void => {
      if (planTimer.current) window.clearTimeout(planTimer.current);
      planTimer.current = window.setTimeout(() => savePlan.mutate(questions), 700);
    },
    [savePlan],
  );
  useEffect(() => () => { if (planTimer.current) window.clearTimeout(planTimer.current); }, []);

  /**
   * DEFECT 44 — delete the job request itself.
   *
   * The API deletes only a request that has nothing hanging off it (no candidates, no scans, no
   * billed lines) and answers 409 with the counts otherwise; that refusal is shown here rather
   * than swallowed, because "delete failed" with no reason is worse than no button at all.
   */
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState<string | null>(null);
  const deleteReq = useMutation({
    mutationFn: () => api.del<{ deleted: boolean }>(`/requisitions/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["requisitions"] });
      navigate("/app/requests");
    },
    onError: (err: unknown) => {
      const body = (err as { body?: { message?: string } })?.body;
      setDeleteMsg(
        body?.message ??
        "Could not delete this request. Refresh and try again, or close it instead.",
      );
    },
  });

  const resetLink = useMutation({
    mutationFn: (candidacyId: string) =>
      api.post<{ inviteUrl: string }>(`/requisitions/${id}/candidates/${candidacyId}/reset`, {}),
    onSuccess: (res, candidacyId) => {
      setAdded((prev) => {
        // Remove old if exists
        const filtered = prev.filter((a) => a.candidacyId !== candidacyId);
        return [{ candidacyId, inviteToken: res.inviteUrl.split("/").pop()!, inviteUrl: res.inviteUrl }, ...filtered];
      });
      void queryClient.invalidateQueries({ queryKey: ["funnel", id] });
    },
  });

  const deleteCandidate = useMutation({
    mutationFn: (candidacyId: string) =>
      api.del(`/requisitions/${id}/candidates/${candidacyId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["funnel", id] });
    },
  });

  // Recruiter-editable organizational tag (migration 0077) — fires immediately on select, unlike
  // the Edit-modal name/email flow. Separate from candidacies.state; see PIPELINE_STATUSES' doc
  // comment (packages/shared/src/types.ts) for why this never touches the Advance/Reject decision.
  const updateStatus = useMutation({
    mutationFn: ({ candidacyId, pipelineStatus }: { candidacyId: string; pipelineStatus: PipelineStatus | null }) =>
      api.patch(`/requisitions/${id}/candidates/${candidacyId}`, { pipelineStatus }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["funnel", id] });
    },
  });

  const trackQuery = useQuery({
    queryKey: ["sourcing-track", id],
    queryFn: () =>
      api.get<{
        engagement: {
          id: string; stage: string; targetScans: number; targetInterviews: number;
          prepaidCredits: number; closeRequested: boolean; refundCredits: number | null;
          shortlist: string[]; channels: { k: string; posted: boolean }[];
          details: { salaryMin?: string; salaryMax?: string; staffAug?: boolean };
          used: { scans: number; interviews: number; credits: number };
        }
      }>(`/requisitions/${id}/sourcing/track`),
    enabled: Boolean(id) && (req?.config.sourcing === true || Boolean(req?.sourcingConvertedAt)),
    retry: false,
  });
  const track = trackQuery.data?.engagement ?? null;
  const requestClose = useMutation({
    mutationFn: () => api.post(`/requisitions/${id}/sourcing/close-request`, {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["sourcing-track", id] }),
  });
  const TRACK_STAGES = ["new", "intake", "posting", "sourcing", "interviewing", "shortlist", "closed"];
  const TRACK_LABEL: Record<string, string> = {
    new: "Request received", intake: "Intake", posting: "Posting the role", sourcing: "Sourcing",
    interviewing: "Interviewing", shortlist: "Shortlist shared", closed: "Closed",
  };

  // ---- Theme 1 header + funnel derived values (display only) ----
  const titleText = req ? displayTitle(req.title) : "Requisition";
  const initials =
    titleText
      .split(/\s+/)
      .filter((w) => /^[A-Za-z]/.test(w))
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join("") || "R";
  // Status tone for the dot in the status control — same mapping as the pill elsewhere.
  const stateTone = req ? statePill(req.state).replace("pill ", "") : "n";
  const budgetText = req ? getRequisitionBudget(req as Parameters<typeof getRequisitionBudget>[0]) : "";
  const budgetIsEstimate = req ? !hasConfiguredBudget(req as Parameters<typeof hasConfiguredBudget>[0]) : false;
  const experienceText = req
    ? getRequisitionExperience({ title: req.title, config: { experience: specDraft.experience, levels: req.config.levels } })
    : "";
  const reqCode = req ? (req.jobCode ?? `REQ-${req.id.replace(/-/g, "").slice(0, 6).toUpperCase()}`) : "";
  const canDelete = !!req && (me?.role === "super_admin" || req.createdBy === me?.userId);
  const langText = (code: string): string => {
    const lang = INTERVIEW_LANGUAGES[code as InterviewLanguageCode];
    if (!lang) return code;
    return lang.nativeLabel && lang.nativeLabel !== lang.label ? `${lang.label} · ${lang.nativeLabel}` : lang.label;
  };

  // "…" menu (Edit / Close / Delete). Delete stays behind its confirm modal (audit X10).
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);
  const specRef = useRef<HTMLElement | null>(null);
  const startEditing = (): void => {
    setIsEditingSpec(true);
    setMenuOpen(false);
    window.setTimeout(() => specRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  // Assessment-criteria card (0099): the requirement's CURRENT versioned set — the same read the
  // editor modal makes (same query key → deduped).
  const reqCompetencies = req?.config.competencies ?? [];
  const assessmentQuery = useAssessment(id);
  const assessmentSet = assessmentQuery.data?.current ?? null;

  return (
    <section className="t1-req">
      <Link className="btn sm ghost t1-back" to="/app/requisitions">
        <span style={{ display: "inline-flex", transform: "rotate(180deg)" }}>
          <Arrow />
        </span>
        All requirements
      </Link>

      {/* ---- Header card: identity, status, meta, actions + funnel ---- */}
      <section className="card t1-hero" aria-label="Requirement summary">
        <div className="t1-hero-top">
          <div className="t1-mark" aria-hidden="true">{initials}</div>
          <div className="t1-hero-id">
            <div className="t1-title-row">
              <h1 title={titleText}>{titleText}</h1>
              {req && (
                <Dropdown
                  ariaLabel={`Requirement status: ${REQ_STATUS_OPTIONS.find((o) => o.value === reqStatusOf(req.state))?.label}`}
                  value={reqStatusOf(req.state)}
                  options={REQ_STATUS_OPTIONS}
                  triggerClassName={`t1-status-ctl tone-${stateTone}`}
                  disabled={changeState.isPending || holdReq.isPending || resumeReq.isPending || reopenReq.isPending}
                  onChange={(next) => {
                    const cur = req.state;
                    if (next === "closed") {
                      setCloseOpen(true);
                    } else if (next === "on_hold") {
                      if (window.confirm("Are you sure you want to put this requirement on hold?")) holdReq.mutate();
                    } else if (next === "draft") {
                      if (window.confirm("Move this requirement back to Draft?")) changeState.mutate("draft");
                    } else if (cur === "on_hold") {
                      resumeReq.mutate();
                    } else if (cur === "closed" || cur === "finalized") {
                      reopenReq.mutate();
                    } else if (cur === "draft") {
                      // Draft → Active: open for candidates (the engine moves it on from here).
                      changeState.mutate("collecting");
                    }
                  }}
                >
                  <span className="t1-status-k">Status</span>
                  <span className="t1-status-dot" aria-hidden="true" />
                  <span className="t1-status-v">{REQ_STATUS_OPTIONS.find((o) => o.value === reqStatusOf(req.state))?.label}</span>
                  <ChevronDown className="t1-status-caret" aria-hidden="true" />
                </Dropdown>
              )}
              {req && (
                <span
                  className={getSourcingType(req).cls}
                  data-tip={
                    req.sourcingConvertedAt
                      ? `Switched from We source on ${new Date(req.sourcingConvertedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}. ${getSourcingType(req).hint}`
                      : getSourcingType(req).hint
                  }
                >
                  {getSourcingType(req).label}
                  {req.sourcingConvertedAt && <span className="mut"> · switched</span>}
                </span>
              )}
            </div>
            {req ? (
              <div className="t1-meta">
                <span className="t1-chip" data-tip="Department · industry">
                  <Briefcase aria-hidden="true" /> {specDraft.department} · {specDraft.industry}
                </span>
                <span className="t1-chip" data-tip="Experience">
                  <UserRound aria-hidden="true" /> {experienceText} experience
                </span>
                <span className="t1-chip" data-tip="Location & work mode">
                  <Globe aria-hidden="true" /> {specDraft.location} · {workModeLabel(specDraft.mode)}
                </span>
                <span
                  className="t1-chip"
                  data-tip={budgetIsEstimate ? "Estimate — no budget entered yet" : "Budget (CTC)"}
                >
                  <IndianRupee aria-hidden="true" /> <b>{budgetText}</b> {req.config.salaryPeriod === "month" ? "per month" : "CTC"}{budgetIsEstimate && <span className="t1-est">est.</span>}
                </span>
                <span className="t1-chip" data-tip={`${req.config.entryMode.replace(/_/g, " ")} · requirement ID`}>
                  <CalendarDays aria-hidden="true" /> Opened{" "}
                  {new Date(req.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · ID {reqCode}
                </span>
                {req.createdByName && (
                  <span className="t1-chip" data-tip="Who created this requirement">
                    <UserRound aria-hidden="true" /> Created by <b>{req.createdByName}</b>
                  </span>
                )}
              </div>
            ) : (
              <p className="t1-sub">Loading requisition…</p>
            )}
          </div>

          <div className="t1-hero-actions">
            {id && req && <PublicShareButton requisitionId={id} canEdit={isOwner && me?.role !== "external_client"} />}
            <button
              type="button"
              className="btn"
              onClick={() => setImportOpen(true)}
            >
              <FileSpreadsheet aria-hidden="true" /> Import Excel
            </button>
            {/* #238: the header opens this requirement's candidate list; adding one moved to "…". */}
            <Link className="btn primary" to={`/app/candidates?reqId=${id}`}>
              <Users aria-hidden="true" /> View candidates
            </Link>
            {req && (
              <div className="t1-menu-wrap" ref={menuRef}>
                <button
                  type="button"
                  className="t1-icon-btn bordered"
                  aria-label="More actions"
                  data-tip={menuOpen ? undefined : "More actions"}
                  data-tip-pos="left"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  onClick={() => setMenuOpen((o) => !o)}
                >
                  <MoreHorizontal aria-hidden="true" />
                </button>
                {menuOpen && (
                  <div className="t1-menu" role="menu">
                    <button type="button" role="menuitem" onClick={startEditing}>
                      <Pencil aria-hidden="true" /> Edit details
                    </button>
                    <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setAddCandidatesModalOpen(true); }}>
                      <Plus aria-hidden="true" /> Add candidate
                    </button>
                    <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setPlanOpen(true); }}>
                      <MessagesSquare aria-hidden="true" /> Interview questions
                    </button>
                    {canClose && (
                      <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setCloseOpen(true); }}>
                        <Check aria-hidden="true" /> Close request…
                      </button>
                    )}
                    {canDelete && (
                      <>
                        <div className="t1-menu-sep" role="separator" />
                        <button
                          type="button"
                          role="menuitem"
                          className="danger"
                          onClick={() => { setMenuOpen(false); setDeleteMsg(null); setDeleteOpen(true); }}
                        >
                          <Trash aria-hidden="true" /> Delete request…
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {req && (
          <>
            {isPost && tranche && (
              <p className="t1-sub" style={{ margin: 0 }}>
                Target {tranche.target} finalists · next tranche {tranche.nextTrancheSize}
              </p>
            )}
            <div
              className="t1-funnel"
              style={{ gridTemplateColumns: ["minmax(0,1fr)", ...funnelRow.slice(1).map(() => "52px minmax(0,1fr)")].join(" ") } as CSSProperties}
            >
              {funnelRow.map((f, i) => {
                const prev = i > 0 ? funnelRow[i - 1]!.n : null;
                const conv = prev === null ? null : prev > 0 ? Math.round((f.n / prev) * 100) : null;
                const dark = !!f.gate && f.n > 0;
                return (
                  <div key={f.label} className="t1-stage-wrap">
                    {i > 0 && (
                      <div className="t1-conv" data-tip={`Conversion from ${funnelRow[i - 1]!.label}`}>
                        <ArrowRight aria-hidden="true" />
                        <span>{conv === null ? "—" : `${conv}%`}</span>
                      </div>
                    )}
                    <Link
                      to={f.href}
                      className={`t1-stage${dark ? " dark" : ""}`}
                      aria-label={`${f.label}: ${f.n}. ${f.view}`}
                    >
                      <span className="lbl">{f.label}</span>
                      <span className="n tnum">{f.n}</span>
                      <span className="sub">{f.gate && f.n > 0 ? "Needs your decision" : f.note}</span>
                    </Link>
                  </div>
                );
              })}
            </div>
            <p className="t1-note">
              Cumulative — each stage counts everyone who has reached it or gone past it, so one
              candidate who has been invited is counted under both {funnelRow[0]?.label} and Invited.
              Arrows show conversion from the previous stage. Click a stage to see who is in it.
            </p>

            {isPost && tranche?.selfHealed && (
              <div className="banner info">
                <Info />
                <div>
                  <b>Self-healing funnel:</b> the engine auto-invited the next ranked tranche to keep this
                  request on target. No action needed.
                </div>
              </div>
            )}
            {isPost && tranche && !tranche.selfHealed && short > 0 && tranche.invited > 0 && (
              <div className="banner warn">
                <Alert />
                <div>
                  <b>
                    Funnel is {short} short of {tranche.target} finalists.
                  </b>{" "}
                  At a {Math.round(tranche.showRate * 100)}% show rate the engine will invite the next{" "}
                  {tranche.nextTrancheSize} ranked candidates automatically at the tranche review.
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {req && (
        <ImportsPanel reqId={id} title={displayTitle(req.title)} canEdit={isOwner && me?.role !== "external_client"} />
      )}

      {editMsg && (
        <div className={`banner ${editMsg.tone}`}>
          <span>{editMsg.text}</span>
        </div>
      )}
      {/* The automatic vertical re-point after a Department / Industry change (#238). When people
          are already assessed the API says exactly what would move; the owner decides. */}
      {repointMsg && (
        <div className={`banner ${repointMsg.tone}`}>
          <span>{repointMsg.text}</span>
          {needsConfirm && repoint.variables?.verticalKey && (
            <button
              type="button"
              className="btn sm"
              disabled={repoint.isPending}
              onClick={() => repoint.mutate({ verticalKey: repoint.variables!.verticalKey, confirm: true })}
            >
              Change the assessment area anyway
            </button>
          )}
        </div>
      )}

      {/* ---- Two-column body: job spec (left) · intake, weights, question plan (right) ---- */}
      {req && (
        <div className="t1-two">
          <section className="card t1-spec" ref={specRef} aria-label="Job specification and screening criteria">
            <div className="t1-card-h">
              <div>
                <h2>Job specification &amp; screening criteria</h2>
                <p>What the AI screens CVs and interviews against</p>
              </div>
              <div className="right">
                {!isEditingSpec ? (
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() => setIsEditingSpec(true)}
                  >
                    <Pencil aria-hidden="true" /> Edit
                  </button>
                ) : (
                  <span className="pill pv">Editing</span>
                )}
              </div>
            </div>

          {/* VIEW MODE */}
          {!isEditingSpec ? (
            <div className="t1-spec-view">
              <div className="t1-fields">
                <div className="t1-f">
                  <span>Experience</span>
                  <b>{experienceText}</b>
                </div>
                <div className="t1-f">
                  <span>Budget (CTC)</span>
                  <b>
                    {budgetText}
                    {budgetIsEstimate && <span className="t1-est" data-tip="No budget entered yet — an estimate for the role">est.</span>}
                  </b>
                </div>
                <div className="t1-f">
                  <span>Location &amp; mode</span>
                  <b>
                    {specDraft.location} · {workModeLabel(specDraft.mode)}
                  </b>
                </div>
                {specDraft.otherCriteria.map((c, i) => (
                  <div className="t1-f" key={`${c.label}-${i}`}>
                    <span>{c.label}</span>
                    <b>{c.value}</b>
                  </div>
                ))}
              </div>

              {specDraft.summary && (
                <>
                  <h3>Role summary</h3>
                  <div className="t1-para">
                    <MarkdownView content={specDraft.summary} />
                  </div>
                </>
              )}

              <h3>
                Must-have skills <span className="t1-count">{specDraft.requiredSkills.length}</span>
              </h3>
              {specDraft.requiredSkills.length > 0 ? (
                <div className="t1-chips">
                  {specDraft.requiredSkills.map((skill) => (
                    <span key={skill} className="t1-chip">
                      {skill}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="t1r-empty">
                  <span>No skills listed yet — CVs are screened on the role summary alone.</span>
                  <button type="button" className="btn sm" onClick={startEditing}>
                    <Plus /> Add skills
                  </button>
                </div>
              )}

              <h3>Interview languages</h3>
              <div className="t1-chips">
                {specDraft.interviewLanguages.map((code) => (
                  <span key={code} className="t1-chip">
                    <Mic aria-hidden="true" /> {langText(code)}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            /* EDIT MODE */
            <div className="spec-review-content">
              {/* Job Title, Department, Industry, Experience */}
              <div className="spec-fields-2col" style={{ marginBottom: 14 }}>
                <div>
                  <label className="field-label-bold">Job title</label>
                  <input
                    type="text"
                    className="text-input-field"
                    value={specDraft.title}
                    onChange={(e) => setSpecDraft((s) => ({ ...s, title: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="field-label-bold">Department</label>
                  <SearchableSelect
                    options={DEPARTMENT_OPTIONS}
                    value={specDraft.department}
                    onChange={(val) => setSpecDraft((s) => ({ ...s, department: val }))}
                    placeholder="Select or type department…"
                  />
                </div>
              </div>

              <div className="spec-fields-2col" style={{ marginBottom: 14 }}>
                <div>
                  <label className="field-label-bold">Industry / domain</label>
                  <SearchableSelect
                    options={INDUSTRY_OPTIONS}
                    value={specDraft.industry}
                    onChange={(val) => setSpecDraft((s) => ({ ...s, industry: val }))}
                    placeholder="Select or type industry…"
                  />
                </div>
                <div>
                  <label className="field-label-bold" htmlFor="req-experience">Experience</label>
                  <ExperienceInput
                    id="req-experience"
                    value={specDraft.experience}
                    onChange={(v) => setSpecDraft((s) => ({ ...s, experience: v }))}
                  />
                </div>
              </div>
              <p className="mut sm nr-hint">
                The assessment area follows the department and industry — it is set for you when you save.
              </p>

              {/* Budget / CTC — currency, min, max, per month / per year */}
              <label className="field-label-bold">Budget (CTC)</label>
              <BudgetFields budget={budgetDraft} onChange={setBudgetDraft} />

              {/* Location & mode — country, city, Remote / Hybrid / Work from office */}
              <label className="field-label-bold">Location &amp; mode</label>
              <LocationFields
                idPrefix="req"
                place={specDraft.place}
                onPlaceChange={(next, countryChanged) => {
                  setSpecDraft((s) => ({ ...s, place: next }));
                  const cur = COUNTRY_CURRENCY[next.country];
                  if (countryChanged && !budgetDraft.min && !budgetDraft.max && cur) setBudgetDraft((b) => ({ ...b, currency: cur }));
                }}
                mode={specDraft.mode}
                onModeChange={(mode) => setSpecDraft((s) => ({ ...s, mode }))}
              />

              {/* Competencies Assessed — the role's vertical's list; "+ Add competency" for more. */}
              <div style={{ marginBottom: 14 }}>
                <label className="field-label-bold">
                  Competencies assessed ({specDraft.competencies.length})
                </label>
                <div style={{ marginTop: 6 }}>
                  <CompetencyPicker
                    variant="add"
                    vertical={vertical}
                    selected={specDraft.competencies}
                    onChange={(next: string[]) => setSpecDraft((s) => ({ ...s, competencies: next }))}
                  />
                </div>
              </div>

              {/* Role Summary & Scope */}
              <div style={{ marginBottom: 14 }}>
                <label className="field-label-bold" htmlFor="spec-role-summary">Role summary &amp; scope</label>
                <MarkdownEditor
                  id="spec-role-summary"
                  value={specDraft.summary}
                  onChange={(next) => setSpecDraft((s) => ({ ...s, summary: next }))}
                  rows={12}
                  placeholder="Paste the job description, then press Tidy formatting — or write it with ### headings and - bullets."
                />
              </div>

              {/* Must-have skills — every newly added skill is checked against the role by AI */}
              <div style={{ marginBottom: 14 }}>
                <label className="field-label-bold">
                  Must-have skills ({specSkills.length})
                </label>
                <div style={{ marginTop: 6 }}>
                  <CheckedSkillChips
                    skills={specDraft.requiredSkills}
                    checks={skillChecks.checks}
                    onRemove={(skill) => {
                      setSpecDraft((s) => ({ ...s, requiredSkills: s.requiredSkills.filter((k) => k !== skill) }));
                      skillChecks.forget(skill);
                    }}
                  />
                </div>

                {/* Suggested Skills from Description / Role */}
                {suggestedSkills.length > 0 && (
                  <div className="t1-suggest">
                    <div style={{ fontSize: 12, fontWeight: 700, color: "var(--teal)", marginBottom: 6 }}>
                      <Lightbulb aria-hidden="true" style={{ width: 14, height: 14, verticalAlign: "-2px", marginRight: 4 }} />
                      Suggested from the job description — click to add
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {suggestedSkills.slice(0, 16).map((skill) => (
                        <button
                          key={skill}
                          type="button"
                          className="btn-suggested-skill-pill"
                          onClick={() => addSpecSkill(skill)}
                        >
                          + {skill}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="spec-add-tag-inline">
                  <input
                    type="text"
                    className="spec-tag-input-field"
                    placeholder="Type a skill (e.g. Chart of Accounts, Power BI)…"
                    value={newSkillInput}
                    maxLength={80}
                    onChange={(e) => setNewSkillInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newSkillInput.trim()) {
                        e.preventDefault();
                        addSpecSkill(newSkillInput);
                        setNewSkillInput("");
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn-add-tag-inline"
                    onClick={() => {
                      if (newSkillInput.trim()) {
                        addSpecSkill(newSkillInput);
                        setNewSkillInput("");
                      }
                    }}
                  >
                    <Plus /> Add skill
                  </button>
                </div>
              </div>

              {/* Voice AI Interview Languages Selection */}
              <div style={{ marginBottom: 14 }}>
                <label className="field-label-bold">
                  Interview languages ({specDraft.interviewLanguages.length})
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8, marginTop: 8 }}>
                  {Object.entries(INTERVIEW_LANGUAGES).map(([code, meta]) => {
                    const isSelected = specDraft.interviewLanguages.includes(code);
                    return (
                      <div
                        key={code}
                        className={`chat-lang-pill ${isSelected ? "selected" : ""}`}
                        style={{ padding: "6px 10px", borderRadius: 6 }}
                        onClick={() => {
                          setSpecDraft((s) => {
                            if (isSelected) {
                              if (s.interviewLanguages.length === 1) return s; // Keep at least one
                              return { ...s, interviewLanguages: s.interviewLanguages.filter((l) => l !== code) };
                            } else {
                              return { ...s, interviewLanguages: [...s.interviewLanguages, code] };
                            }
                          });
                        }}
                      >
                        <span className="lang-check" aria-hidden="true">{isSelected ? <Check style={{ width: 13, height: 13 }} /> : "○"}</span>
                        <span className="lang-label" style={{ fontSize: 12 }}>{meta.label}</span>
                        <span className="lang-native" style={{ fontSize: 12 }}>{meta.nativeLabel}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Other criteria (#238) — visa sponsorship, a notice period when it matters, anything else */}
              <div style={{ marginBottom: 16 }}>
                <label className="field-label-bold">Other criteria</label>
                {specDraft.otherCriteria.length === 0 && (
                  <p className="mut sm" style={{ margin: "2px 0 8px" }}>
                    Anything else a candidate must meet — visa sponsorship, notice period, certifications…
                  </p>
                )}
                {specDraft.otherCriteria.map((c, i) => {
                  const preset = CRITERION_PRESETS.includes(c.label) && c.label !== "Other";
                  const setAt = (patch: Partial<OtherCriterion>) =>
                    setSpecDraft((s) => ({
                      ...s,
                      otherCriteria: s.otherCriteria.map((x, j) => (j === i ? { ...x, ...patch } : x)),
                    }));
                  return (
                    <div key={i} className="t1-crit-row">
                      <Dropdown
                        ariaLabel="Criterion"
                        value={preset ? c.label : "Other"}
                        options={CRITERION_PRESETS.map((p) => ({ value: p, label: p }))}
                        onChange={(label) => setAt({ label: label === "Other" ? "" : label })}
                      />
                      {!preset && (
                        <input
                          type="text"
                          className="text-input-field"
                          aria-label="Criterion name"
                          placeholder="Criterion (e.g. Certification)"
                          maxLength={60}
                          value={c.label}
                          onChange={(e) => setAt({ label: e.target.value })}
                        />
                      )}
                      <input
                        type="text"
                        className="text-input-field"
                        aria-label={`${c.label || "Criterion"} requirement`}
                        placeholder={
                          c.label === "Visa sponsorship"
                            ? "e.g. Not available — must already have the right to work"
                            : c.label === "Notice period"
                              ? "e.g. 30 days or less"
                              : "Requirement"
                        }
                        maxLength={200}
                        value={c.value}
                        onChange={(e) => setAt({ value: e.target.value })}
                      />
                      <button
                        type="button"
                        className="t1-icon-btn"
                        aria-label="Remove criterion"
                        onClick={() => setSpecDraft((s) => ({ ...s, otherCriteria: s.otherCriteria.filter((_, j) => j !== i) }))}
                      >
                        <X aria-hidden="true" />
                      </button>
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="btn sm"
                  disabled={specDraft.otherCriteria.length >= 12}
                  onClick={() =>
                    setSpecDraft((s) => ({ ...s, otherCriteria: [...s.otherCriteria, { label: "Visa sponsorship", value: "" }] }))
                  }
                >
                  <Plus /> Add criterion
                </button>
              </div>

              {/* Edit Actions Footer */}
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", borderTop: "1px solid var(--line)", paddingTop: 14 }}>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => setIsEditingSpec(false)}
                  disabled={updateReq.isPending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn primary"
                  onClick={handleSaveSpec}
                  disabled={
                    updateReq.isPending ||
                    !specDraft.title.trim() ||
                    Boolean(specBudgetProblem) ||
                    skillChecks.checking(specDraft.requiredSkills)
                  }
                >
                  {updateReq.isPending ? "Saving changes…" : "Save changes"}
                </button>
              </div>
            </div>
          )}
          </section>

          <div className="t1-col">
            {/* APPLICATIONS BY EMAIL — this job's own receiving address (0083). Shown only when the
                org actually has email receiving live: advertising an address that nothing is
                collecting would send real applications into a queue no one reads. */}
            {req.jobEmail && emailIntakeLive && (
              <section className="card t1-dark-card" aria-label="Applications by email">
                <div className="t1-card-h">
                  <span className="t1-ico dark" aria-hidden="true">
                    <Mail />
                  </span>
                  <div>
                    <h2>Applications by email</h2>
                    <p>
                      Forward applications here or put it on your job posting — each sender becomes a
                      candidate on this role and any attached CV is extracted. Nothing is scored on arrival.
                    </p>
                  </div>
                </div>
                <div className="t1-addr">
                  <code title={req.jobEmail}>{req.jobEmail}</code>
                  <button
                    type="button"
                    className="t1-copy"
                    aria-label={jobEmailCopied ? "Copied" : "Copy the application email address"}
                    data-tip={jobEmailCopied ? "Copied" : "Copy address"} data-tip-pos="left"
                    onClick={() => {
                      // Only claim "Copied" when the write actually succeeded. The clipboard API
                      // rejects under a denied permission (and in some embedded/headless contexts),
                      // and a button that says Copied while the clipboard still holds something else
                      // sends the recruiter to paste a stale address onto a job posting.
                      navigator.clipboard
                        .writeText(req.jobEmail!)
                        .then(() => {
                          setJobEmailCopied(true);
                          setTimeout(() => setJobEmailCopied(false), 2000);
                        })
                        .catch(() => setJobEmailFailed(true));
                    }}
                  >
                    {jobEmailCopied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                    <span>{jobEmailCopied ? "Copied" : "Copy"}</span>
                  </button>
                </div>
                {jobEmailFailed && (
                  <p className="t1-dark-mute" style={{ margin: "10px 0 0" }}>
                    Your browser blocked the copy — select the address and copy it manually.
                  </p>
                )}
              </section>
            )}

            <section className="card" aria-label="Assessment criteria">
              <div className="t1-card-h">
                <div>
                  <h2>Assessment criteria</h2>
                  <p>
                    {assessmentSet
                      ? `v${assessmentSet.version} · ${assessmentSet.competencies.length} competenc${assessmentSet.competencies.length === 1 ? "y" : "ies"}${(assessmentQuery.data?.outdatedCandidates ?? 0) > 0 ? ` · ${assessmentQuery.data!.outdatedCandidates} candidate(s) on older criteria` : ""}`
                      : "Pick the assessment areas first"}
                  </p>
                </div>
                <div className="right">
                  {assessmentSet ? (
                    <button
                      type="button"
                      className="btn sm"
                      onClick={() => setWeightsOpen(true)}
                    >
                      <Sliders aria-hidden="true" /> Edit criteria
                    </button>
                  ) : (
                    <button type="button" className="btn sm" onClick={startEditing}>
                      <Pencil aria-hidden="true" /> Pick areas
                    </button>
                  )}
                </div>
              </div>
              {assessmentSet && <AssessmentBars set={assessmentSet} />}
            </section>

            <section className="card" aria-label="Interview question plan">
              <div className="t1-card-h" style={{ marginBottom: 0 }}>
                <span className="t1-ico" aria-hidden="true">
                  <ListChecks />
                </span>
                <div>
                  <h2>Interview question plan</h2>
                  <p>
                    {(assessmentSet?.competencies.length ?? reqCompetencies.length) > 0
                      ? `${assessmentSet?.competencies.length ?? reqCompetencies.length} competenc${(assessmentSet?.competencies.length ?? reqCompetencies.length) === 1 ? "y" : "ies"} · ${specDraft.interviewLanguages.map((c) => INTERVIEW_LANGUAGES[c as InterviewLanguageCode]?.label ?? c).join(", ")}${req.config.adaptiveChallengesEnabled === false || !entitlements.features.adaptiveQuestions ? "" : " · adaptive follow-ups"}`
                      : "Evidence-grounded questions calibrated for this role"}
                  </p>
                </div>
                <div className="right">
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() => setPlanOpen(true)}
                  >
                    <MessagesSquare aria-hidden="true" /> Questions
                  </button>
                </div>
              </div>
              {/* Adaptive follow-ups switch — the API (PATCH /adaptive-questions) never went away; the
                  checkbox was lost in a 20 Sep refactor. Off = no live follow-ups and no "a moment ago
                  you said…" check: a shorter interview (less live speech), the planned questions only. */}
              {(() => {
                const canEdit = isOwner && me?.role !== "external_client";
                const adaptiveLocked = !entitlements.features.adaptiveQuestions;
                const resumeLocked = !entitlements.features.resumeQuestions;
                const adaptiveOn = !adaptiveLocked && req.config.adaptiveChallengesEnabled !== false;
                const resumeOn = !resumeLocked && req.config.resumeQuestionsEnabled !== false;
                const lockLine = (
                  <span className="t1r-lockline">
                    <Lock size={13} aria-hidden="true" /> Included in every paid plan — see{" "}
                    <Link to="/app/plans">Plans &amp; usage</Link>
                  </span>
                );
                return (
                  <>
                    <div className={`t1r-adaptive${adaptiveLocked ? " is-locked" : ""}`}>
                      <label className="t1-switch" data-tip={adaptiveLocked ? "Included in every paid plan" : undefined}>
                        <input
                          type="checkbox"
                          checked={adaptiveOn}
                          disabled={adaptiveLocked || setAdaptiveQuestions.isPending || !canEdit}
                          onChange={(e) => setAdaptiveQuestions.mutate(e.target.checked)}
                          aria-describedby="t1r-adaptive-help"
                        />
                        <span className="track" />
                        <span>Adaptive follow-up questions</span>
                        {adaptiveLocked && <Lock size={14} className="t1r-lock" aria-label="Locked" />}
                      </label>
                      <p id="t1r-adaptive-help">
                        {adaptiveLocked
                          ? lockLine
                          : (
                            <>
                              {adaptiveOn
                                ? "On — Asha asks follow-ups on the candidate\u2019s own answers, including a live check against reading from a script. Interviews run longer."
                                : "Off — every candidate gets only the planned questions. Interviews are shorter, and there is no live \u201ca moment ago you said\u2026\u201d check against reading from a script."}{" "}
                              Applies to interviews that start after you change it.
                            </>
                          )}
                      </p>
                      {setAdaptiveQuestions.isError && (
                        <p className="t1r-adaptive-err" role="alert">
                          {serverMessage(setAdaptiveQuestions.error, "Couldn\u2019t save that change — try again.")}
                        </p>
                      )}
                    </div>
                    <div className={`t1r-adaptive t1r-resumeq${resumeLocked ? " is-locked" : ""}`}>
                      <label className="t1-switch" data-tip={resumeLocked ? "Included in every paid plan" : undefined}>
                        <input
                          type="checkbox"
                          checked={resumeOn}
                          disabled={resumeLocked || setResumeQuestions.isPending || !canEdit}
                          onChange={(e) => setResumeQuestions.mutate(e.target.checked)}
                          aria-describedby="t1r-resumeq-help"
                        />
                        <span className="track" />
                        <span>Questions from each candidate&rsquo;s resume</span>
                        {resumeLocked && <Lock size={14} className="t1r-lock" aria-label="Locked" />}
                      </label>
                      <p id="t1r-resumeq-help">
                        {resumeLocked
                          ? lockLine
                          : (
                            <>
                              {resumeOn
                                ? "On — some questions are tailored to each candidate\u2019s own resume (projects, tools, past roles)."
                                : adaptiveOn
                                  ? "Off — everyone gets the shared question plan; only the live follow-ups differ."
                                  : "Off — everyone gets the shared question plan. With adaptive follow-ups also off, every candidate gets identical questions."}{" "}
                              Applies to interviews that start after you change it.
                            </>
                          )}
                      </p>
                      {setResumeQuestions.isError && (
                        <p className="t1r-adaptive-err" role="alert">
                          {serverMessage(setResumeQuestions.error, "Couldn\u2019t save that change — try again.")}
                        </p>
                      )}
                    </div>
                  </>
                );
              })()}
            </section>
          </div>
        </div>
      )}

      {/* Assessment Weights Modal Popup */}
      {weightsOpen && (
        <div
          className="customer-modal-backdrop t1-scrim"
          onClick={() => setWeightsOpen(false)}
          style={{ zIndex: 1100 }}
        >
          <div
            className="questions-modal-card t1-modal"
            style={{ maxWidth: 640 }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Assessment criteria"
          >
            {/* Header */}
            <div className="questions-modal-header">
              <div className="modal-title-box">
                <div className="modal-icon-badge" style={{ background: "var(--teal-wash)", color: "var(--teal)" }}>
                  <Sliders style={{ width: 20, height: 20 }} />
                </div>
                <div>
                  <h3 className="modal-title">Assessment criteria</h3>
                  <p className="modal-sub">
                    What this requirement's interviews are evaluated against. Every change is a new version; interviewed
                    candidates can be re-evaluated from their existing answers.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="t1-icon-btn"
                onClick={() => setWeightsOpen(false)}
                aria-label="Close"
                data-tip="Close"
                data-tip-pos="left"
              >
                <X aria-hidden="true" />
              </button>
            </div>

            <div style={{ padding: "18px 24px 24px", maxHeight: "75vh", overflowY: "auto" }}>
              <AssessmentEditor requisitionId={id} />
            </div>
          </div>
        </div>
      )}



      {data?.requisition.flags?.customPromptEnabled && <JudgePromptCard requisitionId={id} />}

      {/* Sourcing Progress Track */}
      {track && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-h">
            <h3>Sourcing progress</h3>
            <span className="mut sm">everything our desk does on your role, as it happens</span>
          </div>
          <div className="pad" style={{ display: "grid", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {TRACK_STAGES.map((st, i) => {
                const now = TRACK_STAGES.indexOf(track.stage);
                return (
                  <span key={st} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span
                      className="pill"
                      style={{
                        background: i < now ? "var(--good-wash)" : i === now ? "var(--violet-wash)" : "var(--surface-2)",
                        color: i < now ? "var(--good)" : i === now ? "var(--violet)" : "var(--ink-mute)",
                      }}
                    >
                      {TRACK_LABEL[st]}
                    </span>
                    {i < TRACK_STAGES.length - 1 && <span className="mut">›</span>}
                  </span>
                );
              })}
            </div>
            <div className="sm" style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
              <span><b className="tnum">{track.used.scans}</b> of {track.targetScans} resumes screened</span>
              <span><b className="tnum">{track.used.interviews}</b> of {track.targetInterviews} interviews run</span>
              {track.channels.some((c) => c.posted) && (
                <span>live on {track.channels.filter((c) => c.posted).map((c) => c.k).join(", ")}</span>
              )}
              {track.details.staffAug && <span className="pill t">staff-aug open · Hasna MSA</span>}
            </div>
            {track.shortlist.length > 0 && (
              <div className="banner good">
                <span>
                  <b>Your top {track.shortlist.length}:</b> {track.shortlist.join(", ")} — hire any one.
                  Every resume score and interview summary is on this page.
                </span>
              </div>
            )}
            {track.stage === "closed" ? (
              <div className="banner good">
                <span>
                  <b>Closed.</b> Unused resumes and interviews from this package were returned to your
                  organisation — you only paid for the work that actually ran.
                </span>
              </div>
            ) : track.closeRequested ? (
              <div className="sm mut">Close requested — the desk settles and returns anything unused, usually within a day.</div>
            ) : (
              <div>
                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={requestClose.isPending}
                  onClick={() => requestClose.mutate()}
                >
                  Request close & return unused
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {isLoading && <div className="card pad mut sm">Loading the live funnel…</div>}

      {isError && (
        <div className="banner crit">
          <span>Couldn't load this requisition's funnel. It may not exist or you may not have access.</span>
        </div>
      )}

      {!isLoading && !isError && req && (
        <>
          {/* Wave 3: vendor view */}
          {isFulfillingVendor && (
            <div className="banner info" style={{ marginBottom: 16 }}>
              <Users />
              <div>
                <b>Fulfilling for {data?.clientOrgName ?? "a client"}.</b> You source, interview and add
                candidates — the client reviews and makes the final hiring decision.
              </div>
            </div>
          )}

          {/* Wave 3: owner view */}
          {isOwner && isStaffAug && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-h">
                <h3>Staffing vendor</h3>
                <span className="mut sm">
                  {req.fulfilledByOrgId ? "assigned" : "assign this project to a vendor to fulfil"}
                </span>
              </div>
              <div className="card-b">
                {req.fulfilledByOrgId ? (
                  <div className="banner info" style={{ margin: 0 }}>
                    <Info />
                    <div>
                      <b>Fulfilled by {fulfilledByName ?? "your staffing vendor"}.</b> Their recruiters
                      source and interview; you keep the review and the final decision.
                    </div>
                  </div>
                ) : linkedVendors.length === 0 ? (
                  <p className="mut sm" style={{ margin: 0 }}>
                    No staffing vendors linked to your org yet — ask your admin to link a vendor before you
                    can assign this project.
                  </p>
                ) : (
                  <>
                    {assignVendor.isError && (
                      <div className="banner crit" style={{ marginBottom: 10 }}>
                        <span>Couldn't assign this vendor. Please retry in a moment.</span>
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <div style={{ flex: "1 1 220px", minWidth: 220 }}>
                        <Dropdown
                          ariaLabel="Staffing vendor"
                          placeholder="Select a staffing vendor…"
                          value={vendorChoice || null}
                          options={linkedVendors.map((v) => ({ value: v.id, label: v.name }))}
                          onChange={setVendorChoice}
                        />
                      </div>
                      <button
                        className="btn primary sm"
                        disabled={vendorChoice === "" || assignVendor.isPending}
                        onClick={() => assignVendor.mutate(vendorChoice)}
                      >
                        {assignVendor.isPending ? "Assigning…" : "Assign to vendor"}
                      </button>
                    </div>
                    <p className="mut sm" style={{ margin: "10px 0 0" }}>
                      The vendor's recruiters will source and interview candidates for this project. You
                      review every candidate and make the hiring decision.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}



            {importOpen && req && (
              <ImportCandidatesModal reqId={req.id} reqTitle={req.title} onClose={() => setImportOpen(false)} />
            )}

            {/* ---- add more candidates modal ---- */}
            {/* Open for every entry mode (#197). It used to be hidden for 'post' requisitions, but
              the "Connect / Add Candidate" button is shown on all of them — so on a post-mode
              requisition the click did nothing. The server accepts CV uploads and direct adds on
              post requisitions too; they enter the funnel at `invited`, like any direct candidate. */}
            {addCandidatesModalOpen && (
              <ConfirmModal title="Add more candidates" onClose={() => setAddCandidatesModalOpen(false)}>
                <div style={{ marginBottom: 16 }}>
                  <span className="mut sm">with a CV, or by name and email</span>
                </div>
                <div>
                  {/* Deliberately a route into the Candidates hub's uploader rather than a second
                    uploader here. A CV lane bolted onto the funnel would fork the scoring + credit
                    path and leave this requisition holding two grades of candidate; opening the
                    one uploader with this requisition preselected keeps one lane.
                    (#197: this used to link to /app/new?requisition=<id>, but New Request no longer
                    reads that param — the recruiter landed on a blank "new requirement" form.) */}
                  <div
                    className="banner info rd-upload-banner"
                    style={{ marginBottom: 16, alignItems: "center", justifyContent: "space-between", gap: 12 }}
                  >
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <Info />
                      <div>
                        <b>Have their CV? Upload it instead.</b> It's scored against this request's own
                        requirement (one resume from your plan each), and everyone you then pick gets an
                        interview built from their own background (one AI interview each) — the same
                        treatment as your first batch, on this same request.
                      </div>
                    </div>
                    <Link className="btn primary sm" style={{ flexShrink: 0 }} to={`/app/candidates?reqId=${id}&add=upload`}>
                      Upload CVs &amp; score
                    </Link>
                  </div>

                  {added.length > 0 && (
                    <div className="banner info" style={{ marginBottom: 12 }}>
                      <Users />
                      <div>
                        <b>{added.length} invite link{added.length === 1 ? "" : "s"} created.</b> Shown
                        once — copy and send each one to the candidate.
                        {added.map((a) => (
                          <div key={a.candidacyId} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                            {a.name && <b style={{ fontSize: 12.5, flex: "0 0 auto" }}>{a.name}</b>}
                            {a.email ? (
                              <span
                                className="pill v"
                                style={{ fontSize: 12, flex: "0 0 auto", display: "inline-flex", alignItems: "center", gap: 4 }}
                                title={`Invite sent to ${a.email}`}
                              >
                                ✉ {a.email}
                              </span>
                            ) : a.emailed ? (
                              <span className="pill v" style={{ fontSize: 12, flex: "0 0 auto" }}>
                                ✉ Emailed
                              </span>
                            ) : (
                              <span className="pill n" style={{ fontSize: 12, flex: "0 0 auto" }}>
                                no email — share the link
                              </span>
                            )}
                            {/* QA #48: the raw ~120-char URL was noise here too — same fix as the New
                              Request success screen. The token tail (unique per link) is the only
                              part worth showing; the full URL rides the title and the Copy button. */}
                            <div
                              title={a.inviteUrl}
                              style={{ fontFamily: "var(--mono)", fontSize: 12, background: "var(--surface)", padding: "4px 8px", borderRadius: 4, flex: "1 1 200px", border: "1px solid var(--line-soft)" }}
                            >
                              …/interview/{a.inviteUrl.split("/").pop()?.slice(0, 10)}…
                            </div>
                            <button
                              type="button"
                              className="btn ghost sm"
                              aria-label="Copy invite link" data-tip="Copy link" data-tip-pos="left"
                              style={{ padding: "4px 6px", flex: "0 0 auto" }}
                              onClick={() => navigator.clipboard.writeText(a.inviteUrl)}
                            >
                              <Copy className="icon" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div
                    className="banner info"
                    style={{ marginBottom: 16, alignItems: "center", justifyContent: "space-between", gap: 12 }}
                  >
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <FileSpreadsheet style={{ width: 16, height: 16, flexShrink: 0, marginTop: 2 }} />
                      <div>
                        <b>Have a list in Excel?</b> Import it, review who's new, then send the interview link or
                        ask them for their CV and details first.
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn sm"
                      style={{ flexShrink: 0 }}
                      onClick={() => (setAddCandidatesModalOpen(false), setImportOpen(true))}
                    >
                      Import Excel / CSV
                    </button>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 13.5 }}>No CV — invite by name and email</div>
                    <p className="mut sm" style={{ margin: "3px 0 0" }}>
                      One per line, as <b>Name &lt;email&gt;</b>. Nothing is scored and we hold no
                      resume for them, so they're asked this request's shared questions rather than
                      anything drawn from their own experience. Upload a CV above if you'd rather they
                      were screened and questioned on their own background.
                    </p>
                  </div>
                  <div className="field" style={{ marginBottom: 10 }}>
                    <textarea
                      rows={3}
                      value={pasteText}
                      onChange={(e) => setPasteText(e.target.value)}
                      placeholder={"Anjali Nair <anjali.nair@gmail.com>"}
                    />
                  </div>
                  {inviteMore.isError && (
                    <div className="banner crit" style={{ marginBottom: 10 }}>
                      <span>Couldn't add these candidates — check the emails and try again.</span>
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <button
                      className="btn primary sm"
                      disabled={parsed.candidates.length === 0 || inviteMore.isPending}
                      onClick={() => setChargeOpen(true)}
                    >
                      {inviteMore.isPending
                        ? "Inviting…"
                        : `Invite ${parsed.candidates.length || ""} to interview`.replace("  ", " ")}
                    </button>
                    <span className="mut sm">
                      {parsed.candidates.length} parsed
                      {parsed.invalid.length > 0 && ` · ${parsed.invalid.length} skipped`}
                      {addCount > 0 && ` · uses ${addCount} AI interview${addCount === 1 ? "" : "s"}`}
                    </span>
                  </div>
                </div>
              </ConfirmModal>
            )}




          </>
        )
      }

      {
        closeOpen && (
          <ConfirmModal title={`Close “${req?.title ?? "this request"}”?`} onClose={() => setCloseOpen(false)}>
            <p className="mut sm" style={{ marginTop: 0 }}>
              This takes the request off your open list. The shortlist, reports and audit trail stay
              available — nothing is deleted. If a finalist has been chosen it's recorded as finalized.
            </p>
            <div className="field">
              <label htmlFor="close-reason">
                Reason <span className="hint">— optional, for your audit trail</span>
              </label>
              <textarea
                id="close-reason"
                rows={3}
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                placeholder="e.g. Offer accepted / role filled / paused"
                maxLength={500}
              />
            </div>
            {closeReq.isError && (
              <div className="banner crit" style={{ marginBottom: 12 }}>
                <span>Couldn't close this request. Please retry in a moment.</span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button className="btn" onClick={() => setCloseOpen(false)}>
                Cancel
              </button>
              <button
                className="btn primary"
                disabled={closeReq.isPending}
                onClick={() => closeReq.mutate()}
              >
                {closeReq.isPending ? "Closing…" : "Close request"}
              </button>
            </div>
          </ConfirmModal>
        )
      }

      {
        scanChargeOpen && (
          <ConfirmChargeModal
            title="Confirm & send interviews"
            subtitle={`${pickedScanIds.size} screened candidate${pickedScanIds.size === 1 ? "" : "s"} will be invited to a verified AI interview.`}
            unit="interview"
            count={pickedScanIds.size}
            lines={[]}
            confirmLabel={pickedScanIds.size === 1 ? "Send interview" : "Send interviews"}
            busy={inviteScans.isPending}
            error={inviteScans.isError ? serverMessage(inviteScans.error, "Couldn't send these interviews — please try again.") : null}
            onConfirm={() => inviteScans.mutate()}
            onCancel={() => !inviteScans.isPending && setScanChargeOpen(false)}
          />
        )
      }

      {
        chargeOpen && (
          <ConfirmChargeModal
            title="Confirm & send interviews"
            subtitle={`${addCount} verified AI interview${addCount === 1 ? "" : "s"} will be sent.`}
            unit="interview"
            count={addCount}
            lines={[]}
            confirmLabel={addCount === 1 ? "Send interview" : "Send interviews"}
            busy={inviteMore.isPending}
            error={inviteMore.isError ? serverMessage(inviteMore.error, "Couldn't add these candidates — check the emails and try again.") : null}
            onConfirm={() => inviteMore.mutate()}
            onCancel={() => !inviteMore.isPending && setChargeOpen(false)}
          />
        )
      }

      {
        deleteOpen && req && (
          <ConfirmModal title="Delete this job request?" onClose={() => setDeleteOpen(false)}>
            <p style={{ marginTop: 0 }}>
              <b>{displayTitle(req.title)}</b> will be removed from your list. This can't be undone.
            </p>
            <p className="mut sm">
              Requests that already have candidates, screened resumes or billed items can't be
              deleted — close those instead, so the interview records and the billing trail stay
              auditable.
            </p>
            {deleteMsg && (
              <div className="banner warn" style={{ marginTop: 12 }}>
                <Alert />
                <div>{deleteMsg}</div>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button className="btn sm ghost" onClick={() => setDeleteOpen(false)}>
                Keep it
              </button>
              <button
                className="btn sm danger-solid"
                disabled={deleteReq.isPending}
                onClick={() => deleteReq.mutate()}
              >
                {deleteReq.isPending ? "Deleting…" : "Delete request"}
              </button>
            </div>
          </ConfirmModal>
        )
      }

      {
        planOpen && req && (
          <RequirementQuestionsModal
            requisitions={[{ id: req.id, title: req.title }]}
            initialRequisitionId={req.id}
            onClose={() => setPlanOpen(false)}
          />
        )
      }

      {
        questionsFor && (
          <div className="rq-modal-scrim" role="dialog" aria-modal="true" onClick={() => setQuestionsFor(null)}>
            <div className="rq-modal" style={{ maxWidth: 640, width: "94%" }} onClick={(e) => e.stopPropagation()}>
              <div className="rq-modal-h">
                <div className="t">Interview questions</div>
                <div className="d">
                  {questionsFor.name ?? "This candidate"} · what they'll actually be asked
                </div>
              </div>
              <div className="rq-modal-b" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                <CandidacyQuestionsPanel candidacyId={questionsFor.id} vertical={vertical} />
              </div>
              <div className="rq-modal-f">
                <button type="button" className="rq-btn ghost" onClick={() => setQuestionsFor(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }
      {
        editingTarget && (
          <ConfirmModal title="Edit candidate details" onClose={() => setEditingTarget(null)}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateCandidateDetails.mutate(editingTarget);
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 14, margin: "16px 0 20px" }}>
                <div>
                  <label className="mut sm" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                    Candidate Name
                  </label>
                  <input
                    type="text"
                    className="rq-in"
                    style={{ width: "100%" }}
                    placeholder="e.g. Mohammed Ahmed"
                    value={editingTarget.name}
                    onChange={(e) => setEditingTarget({ ...editingTarget, name: e.target.value })}
                    autoFocus
                  />
                </div>
                <div>
                  <label className="mut sm" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    className="rq-in"
                    style={{ width: "100%" }}
                    placeholder="e.g. mohammed.ahmed@example.com"
                    value={editingTarget.email}
                    onChange={(e) => setEditingTarget({ ...editingTarget, email: e.target.value })}
                  />
                </div>

                {/* Replace Resume & Re-scan section */}
                {editingTarget.type === "scan" && (
                  <div style={{ borderTop: "1px solid var(--line-soft)", paddingTop: 14 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      <UploadCloud className="icon sm" /> Replace Resume &amp; Re-scan
                    </div>
                    <p className="mut sm" style={{ margin: "0 0 10px" }}>
                      Upload a new CV to re-extract skills, re-calculate fit score, and update grounding questions.
                    </p>
                    <div
                      style={{
                        border: "1.5px dashed var(--line)",
                        borderRadius: 8,
                        padding: "14px 16px",
                        background: "var(--surface)",
                        textAlign: "center",
                      }}
                    >
                      <input
                        type="file"
                        id="replace-cv-input"
                        accept=".pdf,.docx,.txt"
                        style={{ display: "none" }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const dataUrl = await fileToDataUrl(file);
                          const base64 = dataUrl.split(",")[1] || "";
                          setEditingTarget((prev) =>
                            prev
                              ? {
                                ...prev,
                                filename: file.name,
                                resumeFile: {
                                  filename: file.name,
                                  mime: file.type || "application/pdf",
                                  contentBase64: base64,
                                },
                                reScore: prev.reScore ?? true,
                              }
                              : null,
                          );
                        }}
                      />
                      {editingTarget.filename ? (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{editingTarget.filename}</span>
                          <label
                            htmlFor="replace-cv-input"
                            className="btn ghost sm"
                            style={{ padding: "3px 8px", cursor: "pointer" }}
                          >
                            Change file
                          </label>
                        </div>
                      ) : (
                        <label
                          htmlFor="replace-cv-input"
                          className="btn ghost sm"
                          style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
                        >
                          <UploadCloud className="icon sm" /> Choose new CV (.pdf, .docx, .txt)
                        </label>
                      )}
                    </div>

                    {editingTarget.resumeFile && (
                      <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 12.5, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={editingTarget.reScore !== false}
                          onChange={(e) =>
                            setEditingTarget((prev) => (prev ? { ...prev, reScore: e.target.checked } : null))
                          }
                        />
                        <span>Re-score candidate resume with AI against job requirement</span>
                      </label>
                    )}
                  </div>
                )}

                <p className="mut sm" style={{ margin: 0 }}>
                  Updating candidate details synchronizes across their screened resume, candidate record, and interview invite.
                </p>
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => setEditingTarget(null)}
                  disabled={updateCandidateDetails.isPending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn primary"
                  disabled={updateCandidateDetails.isPending}
                >
                  {updateCandidateDetails.isPending ? "Scanning & Saving…" : "Save changes"}
                </button>
              </div>
            </form>
          </ConfirmModal>
        )
      }
    </section >
  );
}
